import React, { useState, useEffect, useRef } from "react";
import { Info } from "lucide-react";
import { Data } from "../pages/App";
import { getFieldHint } from "../services/api";
import {
  coerceToSingleString,
  coerceToExternalFactorList,
  singleStringToFormField,
} from "../constants/tocFields";

type FormProps = {
  data: Data;
  updateField: (field: keyof Data, value: string) => void;
  highlightField?: string | null; // NEW: Field to highlight
};

type FormField =
  | "activities"
  | "objectives"
  | "aim"
  | "goal"
  | "externalInfluences";

const FORM_FIELDS: FormField[] = [
  "goal",
  "aim",
  "objectives",
  "activities",
  "externalInfluences",
];

export default function FormPanel({
  data,
  updateField,
  highlightField,
}: FormProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [activeHighlight, setActiveHighlight] = useState<string | null>(null);
  const [openHelp, setOpenHelp] = useState<string | null>(null);
  const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const toggleSidebar = () => setIsOpen(!isOpen);

  const fields = FORM_FIELDS;

  const fieldLabels: Record<FormField, string> = {
  goal: "Long-term impact",
  aim: "Medium-term impact(s)",
  objectives: "Short-term impact(s)",
  activities: "Program activities/outputs",
  externalInfluences: "Mapping external factors",
};

  const fieldExamples: Record<FormField, string> = {
  goal: "What is the ‘big picture goal’ your program contributes to?",
  aim: "What medium-term change is your program designed to strengthen?",
  objectives: "What specific change(s) is your program directly responsible for?",
  activities: "What will the program do or produce?",
  externalInfluences:
    "What external factors will influence your ability to deliver the program activities/outputs and the achievement of short-term, medium-term, and long-term impact?",
};

const fieldHelpText: Record<FormField, string[]> = {
  goal: [
    "Long-term impact is about the broader change you want to contribute towards and should be aligned with organisational, funder or community priorities.",
  ],

  aim: [
    "Medium-term impact is within your influence but not owned alone; it is more specific than, and a necessary step toward, long-term impact.",
    "Tip: For your impact to stand up, you need to be able to state how strengthening the medium-term impact(s) logically contribute to long-term impact. You can draw on evidence, prior evaluation, and professional experience to make these links.",
  ],

  objectives: [
    "This is about the change you want to see. Be careful not to include activities and outputs here.",
    "Ideally, these statements should be specific, measurable, achievable, realistic and within a timeframe.",
    "Tip: For your impact to stand up, you need to be able to state how achieving the short-term impact(s) logically contribute to medium-term impact(s). You can draw on evidence, prior evaluation, and professional experience to make these links.",
  ],

  activities: [
    "Think about specific actions, events, processes, or materials your program delivers.",
    "Describe what the program will do or produce.",
    "Include the intended audience of the activity/output.",
    "Consider who you might work with (if it is a collaborative effort) and include the partner in the activity/output.",
    "Tip: For your impact to stand up, you need to be able to state how delivering the activities/outputs logically helps to achieve your short-term impact. You can draw on evidence, prior evaluation, and professional experience to make these links.",
  ],

  externalInfluences: [
    "Even the best-designed programs are shaped by external factors, such as system-level factors, for example policy changes and workforce capacity; community-level factors, for example social norms and cultural context; other services and programs; and wider factors.",
    "Recognising external factors shows strategic awareness and readiness to adapt.",
  ],
};

  const [errors, setErrors] = useState<Record<FormField, string>>({
  activities: "",
  objectives: "",
  aim: "",
  goal: "",
  externalInfluences: "",
});

  // Character limits for input validation
  const maxChars = 200;
  const warningThreshold = 180;

  const [progress, setProgress] = useState(0);

  // ─── AI hint state ─────────────────────────────────────────────────────────
  const [fieldHints, setFieldHints] = useState<Record<string, string>>({});
  const [loadingHint, setLoadingHint] = useState<string | null>(null);
  const hintTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Handle highlighting and scrolling when field is added from visual panel
  useEffect(() => {
    if (highlightField) {
      setActiveHighlight(highlightField);

      // Scroll to the newly added field
      const fieldElement = fieldRefs.current[highlightField];
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      // Remove highlight after animation completes (2 seconds)
      const timer = setTimeout(() => {
        setActiveHighlight(null);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [highlightField]);

  // Calculate completion progress based on filled fields
  useEffect(() => {
    const filledFields = fields.filter((f) => {
      if (f === "externalInfluences") {
        return coerceToExternalFactorList(data[f]).some((v) => v.trim() !== "");
      }
      return coerceToSingleString(data[f]).trim() !== "";
    }).length;
    setProgress((filledFields / fields.length) * 100);
  }, [data, fields]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = hintTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const getImpactFieldText = (field: FormField): string =>
    coerceToSingleString(data[field]);

  /** User-visible text for hints. */
  const getFieldTextForHint = (field: FormField): string => {
    if (field === "externalInfluences") {
      return coerceToExternalFactorList(data[field]).join("\n");
    }
    return getImpactFieldText(field);
  };

  // ─── Manual hint trigger ──────────────────────────────────────────────────
  const triggerHint = async (field: FormField) => {
    const sourceText = getFieldTextForHint(field).trim();
    if (sourceText.length < 4) {
      setFieldHints((prev) => ({
        ...prev,
        [field]: "Start typing first, then I can coach you on this field.",
      }));
      return;
    }
    if (hintTimers.current[field]) clearTimeout(hintTimers.current[field]);
    try {
      setLoadingHint(field);
      const result = await getFieldHint({
        field,
        value: sourceText,
        projectTitle: data.projectTitle,
      });
      if (result?.success && result.data?.hint) {
        setFieldHints((prev) => ({ ...prev, [field]: result.data.hint }));
      } else if (result && result.success === false && result.message) {
        setFieldHints((prev) => ({
          ...prev,
          [field]: `Could not get a hint: ${result.message}`,
        }));
      } else {
        setFieldHints((prev) => ({
          ...prev,
          [field]:
            "No suggestion right now — try adding a bit more detail and tap Hint again.",
        }));
      }
    } catch (err: unknown) {
      let msg = "Something went wrong";
      if (typeof err === "object" && err !== null && "response" in err) {
        const apiMsg = (err as { response?: { data?: { message?: string } } })
          .response?.data?.message;
        if (apiMsg) msg = String(apiMsg);
      }
      if (msg === "Something went wrong" && err instanceof Error) {
        msg = err.message;
      }
      const code =
        typeof err === "object" && err !== null && "code" in err
          ? String((err as { code?: string }).code || "")
          : "";
      const isTimeout =
        code === "ECONNABORTED" ||
        /timeout|timed out|abort/i.test(msg) ||
        (err instanceof Error &&
          (err.name === "AbortError" || err.name === "CanceledError"));
      setFieldHints((prev) => ({
        ...prev,
        [field]: isTimeout
          ? "The hint request timed out. Please try again."
          : `Could not get a hint: ${msg}`,
      }));
    } finally {
      setLoadingHint(null);
    }
  };

  // Validate field input for required and length constraints
  const validateField = (field: FormField, value: string) => {
    if (!value.trim()) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${fieldLabels[field]} is required`,
      }));
    } else if (value.length > maxChars) {
      setErrors((prev) => ({
        ...prev,
        [field]: `Maximum ${maxChars} characters allowed`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className={`sidebar ${isOpen ? "open" : "collapsed"}`}>
      <button className="toggle-btn" onClick={toggleSidebar}>
        {isOpen ? "←" : "→"}
      </button>

      <div className="sidebar-content">
        <div className="form-header">
          <p className="main-title">The tool guides you step by step through each component of the Impact Alignment Map, with prompts and tips to support you along the way.</p>
        </div>

        <div className="form-group">
         <label className="form-label">Program Name</label>
          <input
            value={data.projectTitle}
            onChange={(e) => updateField("projectTitle", e.target.value)}
            placeholder="Enter program name..."
            style={{ width: "100%" }}
          />
        </div>

        {/* Progress bar showing form completion */}
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>
        <p className="progress-text">{Math.round(progress)}% completed</p>

        {fields.map((field) => {
          const fieldText =
            field === "externalInfluences"
              ? data[field] || ""
              : getImpactFieldText(field);

          return (
            <div
              className="form-group"
              key={field}
              id={`step-${field}`}
              ref={(el) => {
                fieldRefs.current[field] = el;
              }} // Store ref for scrolling
            >
<div className="step-title-row">
  <label htmlFor={`field-${field}`} className="form-label">
    {fieldLabels[field]}
  </label>

  <button
    type="button"
    className="learn-more-btn"
    onClick={() => setOpenHelp(openHelp === field ? null : field)}
    title="Learn more"
  >
    <Info size={16} />
  </button>
</div>

<p className="helper-text">{fieldExamples[field]}</p>

{openHelp === field && (
  <div className="learn-more-box">
    <ul className="learn-more-list">
      {fieldHelpText[field].map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </div>
)}

              <div style={{ marginBottom: "8px" }}>
                <textarea
                  id={`field-${field}`}
                  value={fieldText}
                  maxLength={maxChars}
  onChange={(e) => {
  const value = e.target.value;

  if (field === "externalInfluences") {
    const externalItems = value
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter((item) => item !== "");

    if (externalItems.length > 5) {
      setErrors((prev) => ({
        ...prev,
        externalInfluences: "Maximum 5 external factors allowed",
      }));
      return;
    }

    updateField(field, value);
    validateField(field, value);
    return;
  }

    updateField(field, singleStringToFormField(value));
    validateField(field, value);
                  }}
  placeholder={fieldExamples[field]}
  className={`${errors[field] ? "error-input" : ""} ${
    activeHighlight === field ? "newly-added" : ""
  }`}
  style={{ width: "100%" }}
/>
{field === "externalInfluences" && (
  <p className="external-factor-note">
    Use a comma or press Enter to add a new external factor.
  </p>
)}

                  {/* Warning when approaching character limit */}
                  {fieldText.length >= warningThreshold &&
                    fieldText.length <= maxChars && (
                      <span className="warning-text">
                        Approaching character limit ({fieldText.length}/
                        {maxChars})
                      </span>
                    )}
                </div>

              {/* AI hint controls */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: 4,
                }}
              >
                <button
                  type="button"
                  onClick={() => triggerHint(field)}
                  disabled={loadingHint === field}
                  title="Get AI coaching hint"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "3px 8px",
                    borderRadius: "999px",
                    border: "1px solid #e2e0ff",
                    background: loadingHint === field ? "#f0eeff" : "#f8f7ff",
                    color: "#7c3aed",
                    fontSize: "11px",
                    fontWeight: 600,
                    cursor: loadingHint === field ? "not-allowed" : "pointer",
                    flexShrink: 0,
                  }}
                >
                  {loadingHint === field ? "Thinking" : "Hint"}
                </button>
              </div>
              {loadingHint === field && (
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "#a0aec0",
                  }}
                >
                  AI coach thinking...
                </div>
              )}
              {fieldHints[field] && loadingHint !== field && (
                <div
                  onClick={() =>
                    setFieldHints((prev) => ({ ...prev, [field]: "" }))
                  }
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    marginTop: "6px",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #f8f7ff, #f0eeff)",
                    border: "1px solid #e2e0ff",
                    cursor: "pointer",
                  }}
                  title="Click to dismiss"
                >
                  <span style={{ fontSize: "12px", color: "#553C9A", lineHeight: 1.5, flex: 1 }}>
                    {fieldHints[field]}
                  </span>
                  <span style={{ fontSize: "14px", color: "#a0aec0", flexShrink: 0 }}>
                    ×
                  </span>
                </div>
              )}

              {/* Display validation errors */}
              {errors[field] && (
                <span className="error-text">{errors[field]}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
